import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { BadgeCheck, Star } from "lucide-react";
import { collapseRanges, ALL_AGES, GRADES, eventAudienceParts } from "@/lib/sports";
import { EventAudience } from "@/components/EventAudience";
import { formatDateRange, formatTimeRange, todayLocalISO } from "@/lib/formatDate";
import { tzAbbrev } from "@/lib/timezone";
import { eventEndsAt } from "@/lib/calendar";
import { getListPrefs, saveListPrefs } from "@/lib/filterCookies";
// Auto-fit a single-line title: shrinks the font-size until the full text fits
// its container width on one line (never wraps, never truncates). Refits on resize.
export function FitText({ children, after = null, max = 20, min = 11, className = "", testid }) {
    const outer = useRef(null);
    const inner = useRef(null);
    const [size, setSize] = useState(max);
    useLayoutEffect(() => {
        const box = outer.current, txt = inner.current;
        if (!box || !txt) return undefined;
        const fit = () => {
            let s = max;
            txt.style.fontSize = s + "px";
            let guard = 0;
            while (txt.scrollWidth > box.clientWidth && s > min && guard < 100) { s -= 0.5; txt.style.fontSize = s + "px"; guard++; }
            setSize(s);
        };
        fit();
        const ro = new ResizeObserver(fit);
        ro.observe(box);
        return () => ro.disconnect();
    }, [children, after, max, min]);
    return (
        <span ref={outer} className="block w-full overflow-hidden" data-testid={testid}>
            <span ref={inner} className={`inline-block whitespace-nowrap align-bottom ${className}`} style={{ fontSize: size }}>
                {children}
                {after ? <span className="inline-flex items-center align-middle ml-1.5">{after}</span> : null}
            </span>
        </span>
    );
}

// Compact yellow star rating + review count for list cards. Renders nothing with no reviews.
export function StarRating({ rating = 0, count = 0, size = 12, className = "" }) {
    if (!count) return null;
    const r = Math.round(rating);
    return (
        <span className={`inline-flex items-center gap-0.5 align-middle shrink-0 ${className}`} data-testid="listing-rating">
            {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} style={{ width: size, height: size, ...(n <= r ? { fill: "#facc15", color: "#facc15" } : {}) }} className={n <= r ? "" : "text-[#3a3a3a]"} />
            ))}
            <span className="ml-1 text-[11px] text-[#cfcfcf] tabular-nums">{rating} ({count})</span>
        </span>
    );
}

// Section header (icon + coloured label) with optional inline controls on the right.
export function SectionHeader({ label, color, icon: Icon, children }) {
    return (
        <div className="sticky z-30 -mx-6 md:-mx-8 px-6 md:px-8 py-2 bg-[#0a0a0a] flex flex-wrap items-center gap-x-4 gap-y-2 mb-3" style={{ top: "var(--tsi-follows-top, 112px)" }}>
            <div className="flex items-center gap-2 shrink-0">
                {Icon ? <Icon className="h-5 w-5 self-center" style={{ color }} /> : <span className="h-2.5 w-2.5" style={{ background: color }} />}
                <h3 className="font-display text-2xl tracking-wide" style={{ color }}>{label}</h3>
            </div>
            {children}
        </div>
    );
}

// ---- Sort / page-size / pagination (identical to Landing) ----
export function sortItems(items, field, dir) {
    const val = (x) => {
        if (field === "name") return (x.name || x.base_name || "").toLowerCase();
        if (field === "rating") return x.avg_rating ?? x.review_rating ?? x.rating ?? 0;
        if (field === "distance") return x.distance_miles ?? Infinity;
        if (field === "date") return x.event_date || "";
        return 0;
    };
    const s = [...items].sort((a, b) => (val(a) < val(b) ? -1 : val(a) > val(b) ? 1 : 0));
    return dir === "desc" ? s.reverse() : s;
}

export function usePersistedCtrl(key, defaults) {
    const [ctrl, setCtrl] = useState(() => {
        const saved = getListPrefs({})[key] || {};
        return { ...defaults, ...saved, page: 0 };
    });
    useEffect(() => {
        const all = getListPrefs({});
        all[key] = { sort: ctrl.sort, dir: ctrl.dir, pageSize: ctrl.pageSize };
        saveListPrefs(all);
    }, [key, ctrl.sort, ctrl.dir, ctrl.pageSize]);
    return [ctrl, setCtrl];
}

// True on narrow (mobile) viewports; used to shorten select labels to fit the header row.
export function useIsMobile(bp = 640) {
    const [m, setM] = useState(typeof window !== "undefined" ? window.innerWidth < bp : false);
    useEffect(() => {
        const on = () => setM(window.innerWidth < bp);
        window.addEventListener("resize", on);
        return () => window.removeEventListener("resize", on);
    }, [bp]);
    return m;
}

export function ListControls({ ctrl, setCtrl, fields, testid, className = "flex flex-wrap items-center gap-2 mb-3", children }) {
    const mobile = useIsMobile();
    useEffect(() => {
        if (!fields.some(([v]) => v === ctrl.sort)) {
            setCtrl((c) => ({ ...c, sort: fields[0][0], page: 0 }));
        }
    }, [fields, ctrl.sort, setCtrl]);
    return (
        <div className={className} data-testid={testid}>
            <select data-testid={`${testid}-sort`} value={ctrl.sort} onChange={(e) => setCtrl((c) => ({ ...c, sort: e.target.value, page: 0 }))} className="bg-[#141414] border border-[#2a2a2a] text-white text-xs px-2 py-1.5 rounded-none">
                {fields.map(([v, l]) => <option key={v} value={v}>{mobile ? l : `Sort: ${l}`}</option>)}
            </select>
            <button type="button" data-testid={`${testid}-dir`} onClick={() => setCtrl((c) => ({ ...c, dir: c.dir === "asc" ? "desc" : "asc" }))} className="bg-[#141414] border border-[#2a2a2a] text-white text-xs px-2 py-1.5 uppercase tracking-wider">
                {ctrl.dir === "asc" ? "▲ Asc" : "▼ Desc"}
            </button>
            <select data-testid={`${testid}-size`} value={ctrl.pageSize} onChange={(e) => setCtrl((c) => ({ ...c, pageSize: Number(e.target.value), page: 0 }))} className="bg-[#141414] border border-[#2a2a2a] text-white text-xs px-2 py-1.5 rounded-none">
                {[5, 10, 25, 50].map((n) => <option key={n} value={n}>{mobile ? `${n} / pg` : `${n} / page`}</option>)}
            </select>
            {children}
        </div>
    );
}

export function Pager({ ctrl, setCtrl, total, testid }) {
    const pages = Math.ceil(total / ctrl.pageSize);
    if (pages <= 1) return null;
    return (
        <div className="flex items-center justify-center gap-3 mt-4" data-testid={testid}>
            <button type="button" disabled={ctrl.page <= 0} onClick={() => setCtrl((c) => ({ ...c, page: c.page - 1 }))} className="px-3 py-1.5 text-xs uppercase tracking-wider border border-[#2a2a2a] disabled:opacity-40 hover:border-white">Prev</button>
            <span className="text-xs text-[#a0a0a0]">Page {ctrl.page + 1} / {pages}</span>
            <button type="button" disabled={ctrl.page >= pages - 1} onClick={() => setCtrl((c) => ({ ...c, page: c.page + 1 }))} className="px-3 py-1.5 text-xs uppercase tracking-wider border border-[#2a2a2a] disabled:opacity-40 hover:border-white">Next</button>
        </div>
    );
}

// ---- Thumbnail (identical to Landing) ----
function Thumb({ src, fallback, color, bg, fit = "cover" }) {
    const [failed, setFailed] = useState(false);
    const bgClass = bg === "black" ? "bg-black" : bg === "clear" ? "bg-transparent" : "bg-white";
    const fitClass = fit === "contain" ? "object-contain" : "object-cover";
    if (src && !failed) return <img src={src} alt="" onError={() => setFailed(true)} className={`h-14 w-14 ${fitClass} border border-[#2a2a2a] shrink-0 ${bgClass}`} />;
    return <span className="h-14 w-14 shrink-0 flex items-center justify-center border border-[#2a2a2a] font-display text-lg" style={{ color }}>{fallback}</span>;
}

export function ThumbCol({ src, fallback, color, distance, bg, fit }) {
    return (
        <div className="flex flex-col items-center shrink-0 gap-1">
            <Thumb src={src} fallback={fallback} color={color} bg={bg} fit={fit} />
            {distance != null && (
                <span className="text-[11px] leading-none text-[#cfcfcf] tabular-nums" data-testid="row-distance">{Math.round(distance)} mi</span>
            )}
        </div>
    );
}

// Cancelled event chip + auto-delete note (identical to Landing).
function daysUntilAutoDelete(cancelledAt) {
    if (!cancelledAt) return null;
    const del = new Date(cancelledAt).getTime() + 7 * 24 * 3600 * 1000;
    const ms = del - Date.now();
    if (ms <= 0) return 0;
    return Math.ceil(ms / (24 * 3600 * 1000));
}

function CancelledMeta({ e }) {
    const d = daysUntilAutoDelete(e.cancelled_at);
    return (
        <div className="mt-1 flex items-center gap-2 flex-wrap">
            <span className="inline-block text-[9px] leading-none tracking-[0.18em] uppercase font-bold px-1.5 py-1 border border-red-500 text-red-500 bg-red-500/10" data-testid={`event-cancelled-chip-${e.id}`}>Cancelled</span>
            <span className="text-[10px] text-[#a0a0a0]" data-testid={`event-cancelled-autodelete-${e.id}`}>Auto-deletes in {d} day{d === 1 ? "" : "s"}</span>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Green shield-check "claimed / verified owner" mark — same as the team detail page.
export function VerifiedShield({ testid }) {
    return <BadgeCheck className="h-4 w-4 text-[#22c55e] shrink-0" strokeWidth={2.5} data-testid={testid} />;
}

// Per-type card body — renders the exact same content as the Landing result cards.
// `type` is one of: team | trainer | trainer_listing | event | user.
// ---------------------------------------------------------------------------
export function ListingBody({ item, type }) {
    if (type === "team") {
        const ageChips = [...collapseRanges(item.age_groups, ALL_AGES), ...collapseRanges(item.grades, GRADES), ...(item.grad_years || [])].slice(0, 4);
        const g = item.genders || [];
        const gender = g.includes("M") && g.includes("F") ? "Boys · Girls" : g.includes("F") ? "Girls" : g.includes("M") ? "Boys" : "";
        const segs = [
            [item.city, item.state].filter(Boolean).join(", ") || null,
            gender || null,
            ageChips.length ? ageChips.join(" · ") : null,
            item.circuit ? <span key="circuit" className="text-[#ff4500]">{item.circuit}</span> : null,
        ].filter(Boolean);
        return (
            <div className="min-w-0 flex-1 leading-none">
                <FitText className="font-display tracking-wide uppercase group-hover:text-[#ff4500]" after={item.owner_verified ? <VerifiedShield testid={`verified-${item.id}`} /> : null}>{item.name}</FitText>
                <div className="text-xs text-[#a0a0a0] truncate leading-none">
                    {segs.reduce((acc, s, i) => (i === 0 ? [s] : [...acc, " · ", s]), [])}
                </div>
                {item.review_count > 0 && <StarRating rating={item.avg_rating} count={item.review_count} />}
            </div>
        );
    }
    if (type === "trainer" || type === "trainer_listing") {
        const isListing = type === "trainer_listing";
        return (
            <div className="min-w-0 flex-1 leading-none">
                <FitText className="font-display tracking-wide uppercase group-hover:text-[#2196f3]" after={item.owner_verified ? <VerifiedShield testid={`verified-${item.id}`} /> : null}>{item.name}</FitText>
                <div className="text-xs text-[#a0a0a0] truncate leading-none">
                    Trainer{item.sport ? ` · ${item.sport}` : ""} · {isListing ? ([item.city, item.state].filter(Boolean).join(", ") || "Community listing") : ((item.specialties || []).slice(0, 3).join(" · ") || "Personal Trainer")}
                </div>
                {item.review_count > 0 && <StarRating rating={item.avg_rating} count={item.review_count} />}
            </div>
        );
    }
    if (type === "event") {
        const e = item;
        const ee = eventEndsAt(e);
        const evPast = ee ? Date.now() >= ee.getTime() : (e.end_date || e.event_date || "") < todayLocalISO();
        const canc = !!e.cancelled_at;
        const hasAud = eventAudienceParts(e).length > 0;
        const loc = [e.city, e.state].filter(Boolean).join(", ");
        return (
            <div className="min-w-0 flex-1 leading-none">
                <FitText className={`font-display tracking-wide uppercase ${canc ? "text-red-500" : "group-hover:text-[#22c55e]"}`} after={e.owner_verified ? <VerifiedShield testid={`verified-${e.id}`} /> : null}>{e.name}</FitText>
                <div className="text-xs truncate leading-none">
                    {canc ? <span className="hidden sm:inline text-red-500 font-semibold">Cancelled? · </span> : null}
                    <span className={(canc || evPast) ? "text-red-500" : "text-[#a0a0a0]"}>{formatDateRange(e.event_date, e.end_date)}{e.start_time ? ` · ${formatTimeRange(e.start_time, e.end_time)}${tzAbbrev(e.timezone, e.event_date) ? " " + tzAbbrev(e.timezone, e.event_date) : ""}` : ""}</span>
                </div>
                {(hasAud || loc) && (
                    <div className="text-xs text-[#a0a0a0] truncate leading-none">
                        <span className="sm:hidden"><EventAudience e={e} providedOnly />{hasAud && loc ? " · " : null}{loc}</span>
                        <span className="hidden sm:inline"><EventAudience e={e} />{hasAud && loc ? " · " : null}{loc}</span>
                    </div>
                )}
                {e.review_count > 0 && <StarRating rating={e.avg_rating} count={e.review_count} />}
                {canc && <CancelledMeta e={e} />}
            </div>
        );
    }
    // user
    return (
        <div className="min-w-0 flex-1">
            <FitText className="font-display tracking-wide uppercase group-hover:text-[#a855f7]">{item.username ? `${item.username}` : (item.name || "—")}</FitText>
        </div>
    );
}

// Thumbnail source + fallback + colour for a given item/type.
export function thumbFor(item, type) {
    const colors = { team: "#ff4500", trainer: "#2196f3", trainer_listing: "#2196f3", event: "#22c55e", user: "#a855f7" };
    let color = colors[type] || "#ff4500";
    if (type === "event" && item.cancelled_at) color = "#ef4444";
    return { src: item.logo || item.photo, fallback: (item.name || item.username || "?")[0], color };
}
